import { useEffect, useRef } from 'react'
import { useDependencyContext } from '../GanttDependency/DependencyContext'
import { useCriticalPathContext } from '../GanttCriticalPath/CriticalPathContext'
import { GanttRowItem, ganttRowItemService } from '../../GanttRowItem'

type UseRowContextRegistrationParams = {
  rowIndex: number
  rowItem: GanttRowItem | undefined
  projectPlanUuid?: string
  showDependency?: boolean
}

/**
 * Registers row date/info with DependencyContext and CriticalPathContext,
 * and notifies CriticalPathContext when the row is unmounted (collapsed/hidden).
 */
export const useRowContextRegistration = ({
  rowIndex,
  rowItem,
  projectPlanUuid,
  showDependency = true,
}: UseRowContextRegistrationParams): void => {
  const dependencyContext = useDependencyContext()
  const criticalPathContext = useCriticalPathContext()

  // Register row dates with DependencyContext for dependency line drawing
  useEffect(() => {
    if (
      showDependency &&
      dependencyContext &&
      rowIndex !== undefined &&
      rowIndex !== null &&
      rowIndex >= 0
    ) {
      const scheduledStart = rowItem?.scheduledDate?.startDate
        ? new Date(rowItem.scheduledDate.startDate)
        : null
      const scheduledEnd = rowItem?.scheduledDate?.endDate
        ? new Date(rowItem.scheduledDate.endDate)
        : null
      dependencyContext.updateRowDates(rowIndex, {
        scheduledStart,
        scheduledEnd,
        wbsItemUuid: rowItem?.wbsItemUuid,
        projectPlanUuid,
      })
    }
  }, [
    showDependency,
    dependencyContext,
    rowIndex,
    rowItem?.scheduledDate?.startDate,
    rowItem?.scheduledDate?.endDate,
    rowItem?.wbsItemUuid,
    projectPlanUuid,
  ])

  // Register row info with CriticalPathContext
  useEffect(() => {
    if (
      criticalPathContext &&
      rowIndex !== undefined &&
      rowIndex !== null &&
      rowIndex >= 0
    ) {
      const isProcess = ganttRowItemService.isProcess(rowItem)
      const wbsItemUuid = rowItem?.wbsItemUuid ?? null
      criticalPathContext.updateRowInfo(rowIndex, {
        isProcess,
        wbsItemUuid,
        projectPlanUuid,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ganttRowItemService.isProcess() は wbsItemType と type のみ参照するため、サブプロパティで十分
  }, [
    criticalPathContext,
    rowIndex,
    rowItem?.wbsItemType,
    rowItem?.type,
    rowItem?.wbsItemUuid,
    projectPlanUuid,
  ])

  // Notify context when cell is unmounted (row collapsed/hidden).
  // Uses refs to capture latest values without adding effect dependencies.
  const cpContextRef = useRef(criticalPathContext)
  cpContextRef.current = criticalPathContext
  const wbsItemUuidRef = useRef(rowItem?.wbsItemUuid)
  wbsItemUuidRef.current = rowItem?.wbsItemUuid
  useEffect(() => {
    return () => {
      const uuid = wbsItemUuidRef.current
      if (uuid) {
        cpContextRef.current?.notifyRowRemoved(uuid)
      }
    }
  }, [])
}
